import type { AppRole, AppRoleEntry } from './types'

/**
 * Numeric rank per `AppRole`. NONE is `0` so `APP_ROLE_RANK[role]` is
 * truthy iff the role grants any access — a quick boolean cast for
 * "does the user have any access to this app at all?". Mirrors the
 * server-side rank in apps/multipass/src/managers/app-role.ts (shifted
 * up by one to give NONE a numeric slot).
 */
export const APP_ROLE_RANK: Record<AppRole, number> = {
  NONE: 0,
  VIEWER: 1,
  EDITOR: 2,
  ADMIN: 3,
}

/**
 * Structural slice of `PublicUser` the role utilities depend on. Apps
 * with their own local user shape (e.g. comms/tasks mirroring multipass
 * into a Prisma row) can pass it directly as long as it exposes a
 * `roles` map.
 */
export interface UserWithRoles {
  roles: Record<string, AppRole>
}

/**
 * Resolve the current effective role for `appSlug` on `user`. Returns
 * `'NONE'` for a null user or an app the user has no grant for. Never
 * returns undefined — callers can compare or rank without nullish guards.
 */
export function getAppRole(user: UserWithRoles | null | undefined, appSlug: string): AppRole {
  return user?.roles?.[appSlug] ?? 'NONE'
}

/**
 * True when `user` has at least `minRole` on `appSlug`. With NONE at
 * rank 0, `hasAppRole(user, slug, 'VIEWER')` is the canonical "any
 * access" check, and `hasAppRole(user, slug, 'NONE')` is always true.
 */
export function hasAppRole(user: UserWithRoles | null | undefined, appSlug: string, minRole: AppRole): boolean {
  return APP_ROLE_RANK[getAppRole(user, appSlug)] >= APP_ROLE_RANK[minRole]
}

/** Convenience: any non-NONE role on `appSlug`. */
export function hasAppAccess(user: UserWithRoles | null | undefined, appSlug: string): boolean {
  return APP_ROLE_RANK[getAppRole(user, appSlug)] > 0
}

/** Convenience: ADMIN on `appSlug`. */
export function isAppAdmin(user: UserWithRoles | null | undefined, appSlug: string): boolean {
  return getAppRole(user, appSlug) === 'ADMIN'
}

/**
 * Structural slice of `PublicUser` the enriched-launcher utility depends
 * on. The new `appRoles` field is optional on `PublicUser` (older
 * multipass deploys may not populate it), so callers should fall back
 * gracefully when the user has no entries — `getAccessibleApps` does
 * exactly that, returning an empty array.
 */
export interface UserWithAppRoles {
  appRoles?: Record<string, AppRoleEntry>
}

/**
 * Apps the user has any access to (`role !== 'NONE'`), with their
 * canonical branding (name, url, icon, colors). Reads from the enriched
 * `appRoles` map; consumers use this to render an "other apps you can
 * access" launcher without a second fetch. Returns an empty array when
 * `appRoles` is absent — the caller is responsible for any fallback
 * (e.g. fetching the legacy app catalog from HQ for older sessions).
 *
 * **Note:** this returns *every* accessible app, including ones admins
 * have marked `hidden`. Most launcher UIs want `getLauncherApps` (below)
 * — it adds the hidden filter and a stable sort. Use
 * `getAccessibleApps` only when you need the unfiltered list (admin
 * tooling, "all apps you have access to" reports).
 */
export function getAccessibleApps(user: UserWithAppRoles | null | undefined): AppRoleEntry[] {
  if (!user?.appRoles) {
    return []
  }
  return Object.values(user.appRoles).filter((entry) => entry.role !== 'NONE')
}

/**
 * Canonical "apps in this user's launcher" list. Every Ordo app that
 * renders a sidebar app rail, app switcher, or launcher tile grid
 * should call this — it's the single rule for "does this app belong
 * in a switch-to-another-app surface?".
 *
 *   - Drops apps the user has no access to (`role === 'NONE'`)
 *   - Drops apps multipass marks `hidden` (admin opted out)
 *   - Returns in the admin-set launcher order (`sortOrder`, set in HQ →
 *     Apps → Sort); unordered apps follow, by name (case-insensitive)
 *
 * Hosts
 * with extra rules (HQ skips slug='auth' because multipass admin has
 * no end-user UI) layer their own filter on top.
 */
export function getLauncherApps(user: UserWithAppRoles | null | undefined): AppRoleEntry[] {
  return getAccessibleApps(user)
    .filter((entry) => !entry.hidden)
    .sort(compareLauncherOrder)
}

/** Admin-set `sortOrder` first (unset last), then name. Exported so hosts
 *  that merge in synthetic entries keep the same order. */
export function compareLauncherOrder(
  a: Pick<AppRoleEntry, 'name' | 'sortOrder'>,
  b: Pick<AppRoleEntry, 'name' | 'sortOrder'>,
): number {
  const ao = a.sortOrder ?? Number.POSITIVE_INFINITY
  const bo = b.sortOrder ?? Number.POSITIVE_INFINITY
  if (ao !== bo) {
    return ao < bo ? -1 : 1
  }
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
}
